/**
 * Subscription merge pipeline (canonical data layer).
 */
import moment from 'moment';
import { getDateServiceConfig } from '../config';
import { pickCmsPageImageUri } from '../../../shared/utils/resolveCmsImageUri';
import { resolveImageUri } from '../../../shared/utils/resolveImageUri';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { parseSubscriptionApiDate, getDeliveryDatesConsumedByDoubleBox, collectDoubleBoxAnchorDates } from './helpers';
import type { SubscriptionDataResponse, SubscriptionData, DayConfig, DateServiceConfig } from './types';

// ---------------------------------------------------------------------------
// Raw API types (from legacy subscriptionApi)
// ---------------------------------------------------------------------------

export interface ParticularItemRaw {
  particular_id?: number;
  date?: string;
  status?: string;
  selected_meals?: number;
  selected_snacks?: number;
  address_id?: number;
  macronutrient?: { calories?: number; protein?: number; fats?: number; carbs?: number; fiber?: number };
}

export interface ParticularsAggregateRaw {
  calories?: number;
  protein?: number;
  fats?: number;
  carbs?: number;
  fiber?: number;
}

export interface DetailRaw {
  title?: string;
  a_title?: string;
  arabic_title?: string;
  description?: string;
  arabic_description?: string;
  day?: number;
  day_left?: number;
  day_off?: number | number[];
  allowed_freeze_days?: number;
  cost?: number;
  total_meals?: number;
  total_snacks?: number;
  force_fixed_menu?: boolean;
  is_subscription_upgraded?: boolean;
  is_upgradable?: boolean;
  is_variable_carbs?: boolean;
  maximum_protein?: number;
  maximum_carbs?: number;
  default_carbs?: number;
  free_consultations?: number;
  upgradable_meal_types?: Array<{
    meal_type_id?: number;
    meal_type_name?: string;
    a_name?: string;
    maximum_selection_limit?: number;
    price_per_item?: number;
    type?: string;
  }>;
  upgradable_food_types?: Array<{
    food_type_id?: number;
    food_type_name?: string;
    a_name?: string;
    maximum_selection_limit?: number;
    price_per_item?: number;
    type?: string;
  }>;
  selection_limit?: Record<string, number> | unknown[];
  minimum_calories?: number;
  maximum_calories?: number;
  image?: string;
  thumbnail?: string;
  /** Plan duration (days); some payloads use `days` instead of `day`. */
  days?: number;
}

/** customer_subscriptions/new – can be flat array (id, thumbnail, detail, particulars aggregate) OR type_name/type_data. */
export interface CustomerSubscriptionsNewApiResponse {
  last_selected_menu_items_date?: string | null;
  subscriptions?: Array<
    | {
        type_name?: string;
        type_data?: Array<{
          id: number;
          slug?: string;
          identifier?: string;
          customer_id?: number;
          cost?: number;
          status?: string;
          start_date?: string;
          end_date?: string;
          detail?: DetailRaw;
          freeze_dates?: string[];
          paused_dates?: string[];
          skipped_dates?: string[];
          particulars?: ParticularItemRaw[] | ParticularsAggregateRaw;
          thumbnail?: string;
          attachments?: string;
          [key: string]: unknown;
        }>;
      }
    | {
        id: number;
        status?: string;
        used_freeze_count?: number;
        start_date?: string;
        end_date?: string;
        detail?: DetailRaw;
        freeze_dates?: string[];
        paused_dates?: string[];
        skipped_dates?: string[];
        double_box_dates?: string[];
        particulars?: ParticularsAggregateRaw;
        thumbnail?: string;
        attachments?: string;
        [key: string]: unknown;
      }
  >;
}

/**
 * my-customer-subscriptions (subData).
 * Console + foodna samples use `type_name` / `type_data` (same as customer_subscriptions).
 * Older flat `{ id, … }` rows are still accepted.
 */
export interface CustomerSubscriptionsFlatApiResponse {
  last_selected_menu_items_date?: string | null;
  subscriptions?: Array<
    | {
        type_name?: string;
        type_data?: Array<{
          id: number;
          detail?: DetailRaw;
          freeze_dates?: string[];
          paused_dates?: string[];
          skipped_dates?: string[];
          double_box_dates?: string[];
          particulars?: ParticularItemRaw[] | ParticularsAggregateRaw;
          thumbnail?: string;
          attachments?: string;
          [key: string]: unknown;
        }>;
      }
    | {
        id: number;
        status?: string;
        used_freeze_count?: number;
        start_date?: string;
        end_date?: string;
        detail?: DetailRaw;
        freeze_dates?: string[];
        paused_dates?: string[];
        skipped_dates?: string[];
        double_box_dates?: string[];
        particulars?: ParticularsAggregateRaw;
        thumbnail?: string;
        attachments?: string;
        [key: string]: unknown;
      }
  >;
}

/** customer_subscriptions (mySubscriptions) – type_name/type_data; items may include slug/identifier and **`particulars`** (array or aggregate). */
export interface CustomerSubscriptionsListApiResponse {
  last_selected_menu_items_date?: string | null;
  subscriptions?: Array<{
    type_name?: string;
    type_data?: Array<{
      id: number;
      slug?: string;
      identifier?: string;
      customer_id?: number;
      coupon_id?: number;
      is_default_assigned?: boolean;
      cost?: number;
      discount_cost?: number;
      final_cost?: number;
      status?: string;
      used_freeze_count?: number;
      start_date?: string;
      end_date?: string;
      charge_url?: string;
      payment_mode?: string;
      payment_status?: string;
      payment_captured_at?: string;
      detail?: DetailRaw;
      freeze_dates?: string[];
      paused_dates?: string[];
      skipped_dates?: string[];
      particulars?: ParticularItemRaw[] | ParticularsAggregateRaw;
      coupon?: string;
      used_free_consultations?: number;
      used_free_head_consultations?: number;
      [key: string]: unknown;
    }>;
  }>;
}

/** Merged raw subscription: all unique fields from the 3 responses, one per id. Particulars split into macros vs daily. */
export interface MergedRawSubscription {
  id: number;
  customer_id?: number;
  slug?: string;
  identifier?: string;
  cost?: number;
  discount_cost?: number;
  final_cost?: number;
  coupon_id?: number;
  status?: string;
  is_default_assigned?: boolean;
  used_freeze_count?: number;
  start_date?: string;
  end_date?: string;
  charge_url?: string;
  payment_mode?: string;
  payment_status?: string;
  payment_captured_at?: string;
  detail?: DetailRaw;
  freeze_dates: string[];
  double_box_dates: string[];
  /** Top-level API pause/skip date lists (foodna; omit → []). */
  paused_dates: string[];
  skipped_dates: string[];
  /** Macro aggregate (protein, carbs, calories, etc.) from one API. */
  particularsMacros?: ParticularsAggregateRaw;
  /** Per-day entries from the other API. */
  particularsDaily?: ParticularItemRaw[];
  thumbnail?: string;
  attachments?: string;
  coupon?: string;
  used_free_consultations?: number;
  used_free_head_consultations?: number;
  image?: string;
  subscription_detail?: unknown;
  title?: string;
  name?: string;
  description?: string;
  category?: string;
}

function parseApiDate(value: string | undefined): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  const m = moment(value, ['DD MMMM, YYYY', 'YYYY-MM-DD', 'DD MMM YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : value;
}

function getDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = moment(startDate, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(endDate, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (!current.isValid() || !end.isValid()) {
    return dates;
  }
  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }
  return dates;
}

/** day_off to array 0-6 (moment.day(): Sun=0, Sat=6). */
function normalizeDayOff(dayOff: number | number[] | undefined): number[] {
  if (dayOff == null) {
    return [];
  }
  if (Array.isArray(dayOff)) {
    return dayOff.map(Number).filter(d => d >= 0 && d <= 6);
  }
  return Number(dayOff) >= 0 && Number(dayOff) <= 6 ? [Number(dayOff)] : [];
}

/** Build Record<meal_type_id, limit> from API selection_limit (object {"289": 3, ...} or array). */
function buildSelectionLimitRecord(selection_limit: unknown): Record<number, number> {
  if (selection_limit && typeof selection_limit === 'object' && !Array.isArray(selection_limit)) {
    return Object.fromEntries(
      Object.entries(selection_limit as Record<string, number>).map(([k, v]) => [Number(k), Number(v)]),
    ) as Record<number, number>;
  }
  if (Array.isArray(selection_limit)) {
    return Object.fromEntries(
      (selection_limit as [unknown, unknown][]).map(([k, v]) => [Number(k), Number(v)]),
    ) as Record<number, number>;
  }
  return {};
}

function hasNonEmptySelectionLimit(selection_limit: unknown): boolean {
  return Object.keys(buildSelectionLimitRecord(selection_limit)).length > 0;
}

/**
 * Merge `detail` objects without letting later APIs clobber food-type limits.
 * Prefer first non-empty `selection_limit` from merge order:
 * `customer_subscriptions/new` → `my-customer-subscriptions` → `customer_subscriptions`.
 * Also aliases `a_title` ↔ `arabic_title` so console/foodna title keys both work for UI.
 */
function mergeSubscriptionDetail(existing: DetailRaw | undefined, incoming: DetailRaw): DetailRaw {
  const merged = { ...(existing ?? {}), ...incoming } as DetailRaw;
  const existing_limit = existing?.selection_limit;
  const incoming_limit = incoming.selection_limit;
  if (hasNonEmptySelectionLimit(existing_limit)) {
    merged.selection_limit = existing_limit;
  } else if (hasNonEmptySelectionLimit(incoming_limit)) {
    merged.selection_limit = incoming_limit;
  } else if (existing_limit !== undefined) {
    merged.selection_limit = existing_limit;
  } else if (incoming_limit !== undefined) {
    merged.selection_limit = incoming_limit;
  } else {
    delete merged.selection_limit;
  }
  const arabic = merged.arabic_title ?? merged.a_title;
  if (arabic) {
    merged.arabic_title = arabic;
    merged.a_title = merged.a_title ?? arabic;
  }
  return merged;
}

/** Normalize API date-list fields (`freeze_dates`, `paused_dates`, …) to unique YYYY-MM-DD strings. */
function asYmdDateList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: string[] = [];
  for (const entry of value) {
    const raw_s = String(entry ?? '').trim();
    if (!raw_s) {
      continue;
    }
    out.push(parseApiDate(raw_s) || raw_s);
  }
  return [...new Set(out)];
}

/** Shared field pick from one customer-subscription row (flat or type_data item). */
function partialFromCustomerSubscriptionItem(item: Record<string, unknown>): Partial<MergedRawSubscription> | null {
  const id = item.id as number | undefined;
  if (id == null) {
    return null;
  }
  const particulars = item.particulars;
  const particulars_is_array = Array.isArray(particulars);
  return {
    id,
    slug: item.slug as string | undefined,
    identifier: item.identifier as string | undefined,
    customer_id: item.customer_id as number | undefined,
    cost: item.cost as number | undefined,
    discount_cost: item.discount_cost as number | undefined,
    final_cost: item.final_cost as number | undefined,
    coupon_id: item.coupon_id as number | undefined,
    status: item.status as string | undefined,
    is_default_assigned: item.is_default_assigned as boolean | undefined,
    used_freeze_count: item.used_freeze_count as number | undefined,
    start_date: item.start_date as string | undefined,
    end_date: item.end_date as string | undefined,
    charge_url: item.charge_url as string | undefined,
    payment_mode: item.payment_mode as string | undefined,
    payment_status: item.payment_status as string | undefined,
    payment_captured_at: item.payment_captured_at as string | undefined,
    detail: item.detail as DetailRaw | undefined,
    freeze_dates: asYmdDateList(item.freeze_dates),
    double_box_dates: asYmdDateList(item.double_box_dates),
    paused_dates: asYmdDateList(item.paused_dates),
    skipped_dates: asYmdDateList(item.skipped_dates),
    particularsMacros:
      particulars != null && typeof particulars === 'object' && !particulars_is_array
        ? (particulars as ParticularsAggregateRaw)
        : undefined,
    particularsDaily: particulars_is_array ? (particulars as ParticularItemRaw[]) : undefined,
    image: item.image as string | undefined,
    thumbnail: item.thumbnail as string | undefined,
    attachments: item.attachments as string | undefined,
    coupon: item.coupon as string | undefined,
    used_free_consultations: item.used_free_consultations as number | undefined,
    used_free_head_consultations: item.used_free_head_consultations as number | undefined,
  };
}

function addPartialToMap(map: Map<number, Partial<MergedRawSubscription>>, partial: Partial<MergedRawSubscription> | null) {
  if (!partial || partial.id == null) {
    return;
  }
  map.set(partial.id, partial);
}

/** Walk `type_name` / `type_data` groups; optionally only Active Subscription. */
function flattenTypeDataGroupsToMap(
  subs: Array<Record<string, unknown>>,
  options?: { active_only?: boolean },
): Map<number, Partial<MergedRawSubscription>> {
  const map = new Map<number, Partial<MergedRawSubscription>>();
  const active_only = options?.active_only !== false;
  for (const group of subs) {
    const type_name = String(group.type_name ?? '');
    if (active_only && type_name !== 'Active Subscription') {
      continue;
    }
    const type_data = group.type_data;
    if (!Array.isArray(type_data)) {
      continue;
    }
    for (const item of type_data) {
      if (item && typeof item === 'object') {
        addPartialToMap(map, partialFromCustomerSubscriptionItem(item as Record<string, unknown>));
      }
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Flatten: each response -> list of items by id (for merge)
// ---------------------------------------------------------------------------

function flattenSubscriptionsNewResponseToMapById(data: CustomerSubscriptionsNewApiResponse | undefined): Map<number, Partial<MergedRawSubscription>> {
  const map = new Map<number, Partial<MergedRawSubscription>>();
  const subs = (data?.subscriptions ?? []) as Array<Record<string, unknown>>;
  if (subs.length === 0) {
    return map;
  }

  const first = subs[0];
  const is_flat_array = first != null && typeof first === 'object' && 'id' in first && !('type_data' in first);

  if (is_flat_array) {
    for (const item of subs) {
      addPartialToMap(map, partialFromCustomerSubscriptionItem(item));
    }
    return map;
  }

  return flattenTypeDataGroupsToMap(subs, { active_only: true });
}

function flattenSubscriptionsFlatResponseToMapById(data: CustomerSubscriptionsFlatApiResponse | undefined): Map<number, Partial<MergedRawSubscription>> {
  const subs = (data?.subscriptions ?? []) as Array<Record<string, unknown>>;
  if (subs.length === 0) {
    return new Map();
  }

  const first = subs[0];
  const is_flat_array = first != null && typeof first === 'object' && 'id' in first && !('type_data' in first);

  if (is_flat_array) {
    const map = new Map<number, Partial<MergedRawSubscription>>();
    for (const item of subs) {
      addPartialToMap(map, partialFromCustomerSubscriptionItem(item));
    }
    return map;
  }

  /** Both console + foodna `my-customer-subscriptions` return type_name/type_data groups. */
  return flattenTypeDataGroupsToMap(subs, { active_only: true });
}

function flattenSubscriptionsListResponseToMapById(data: CustomerSubscriptionsListApiResponse | undefined): Map<number, Partial<MergedRawSubscription>> {
  return flattenTypeDataGroupsToMap((data?.subscriptions ?? []) as Array<Record<string, unknown>>, {
    active_only: true,
  });
}

/** Merge the 3 flattened maps by id: one MergedRawSubscription per id with all unique fields (no duplicates). */
function mergeSubscriptionMapsById(
  flat1: Map<number, Partial<MergedRawSubscription>>,
  flat2: Map<number, Partial<MergedRawSubscription>>,
  flat3: Map<number, Partial<MergedRawSubscription>>,
): MergedRawSubscription[] {
  const allIds = new Set<number>([...flat1.keys(), ...flat2.keys(), ...flat3.keys()]);
  const result: MergedRawSubscription[] = [];

  for (const id of allIds) {
    const r1 = flat1.get(id);
    const r2 = flat2.get(id);
    const r3 = flat3.get(id);

    const merged: MergedRawSubscription = {
      id,
      freeze_dates: [],
      double_box_dates: [],
      paused_dates: [],
      skipped_dates: [],
    };

    const sources = [r1, r2, r3].filter(Boolean) as Partial<MergedRawSubscription>[];
    let particularsDaily: ParticularItemRaw[] | null = null;
    const particularsMacros: ParticularsAggregateRaw = {};

    for (const s of sources) {
      if (s.customer_id != null) {merged.customer_id = s.customer_id;}
      if (s.slug != null) {merged.slug = s.slug;}
      if (s.identifier != null) {merged.identifier = s.identifier;}
      if (s.cost != null) {merged.cost = s.cost;}
      if (s.discount_cost != null) {merged.discount_cost = s.discount_cost;}
      if (s.final_cost != null) {merged.final_cost = s.final_cost;}
      if (s.coupon_id != null) {merged.coupon_id = s.coupon_id;}
      if (s.status != null) {merged.status = s.status;}
      if (s.is_default_assigned != null) {merged.is_default_assigned = s.is_default_assigned;}
      if (s.used_freeze_count != null) {merged.used_freeze_count = s.used_freeze_count;}
      if (s.start_date != null) {merged.start_date = s.start_date;}
      if (s.end_date != null) {merged.end_date = s.end_date;}
      if (s.charge_url != null) {merged.charge_url = s.charge_url;}
      if (s.payment_mode != null) {merged.payment_mode = s.payment_mode;}
      if (s.payment_status != null) {merged.payment_status = s.payment_status;}
      if (s.payment_captured_at != null) {merged.payment_captured_at = s.payment_captured_at;}
      if (s.detail != null) {
        merged.detail = mergeSubscriptionDetail(merged.detail, s.detail);
      }
      if (Array.isArray(s.freeze_dates) && s.freeze_dates.length > 0) {
        merged.freeze_dates = [...new Set([...merged.freeze_dates, ...s.freeze_dates])];
      }
      if (Array.isArray(s.double_box_dates) && s.double_box_dates.length > 0) {
        merged.double_box_dates = [...new Set([...merged.double_box_dates, ...s.double_box_dates])];
      }
      if (Array.isArray(s.paused_dates) && s.paused_dates.length > 0) {
        merged.paused_dates = [...new Set([...merged.paused_dates, ...s.paused_dates])];
      }
      if (Array.isArray(s.skipped_dates) && s.skipped_dates.length > 0) {
        merged.skipped_dates = [...new Set([...merged.skipped_dates, ...s.skipped_dates])];
      }
      if (Array.isArray(s.particularsDaily) && s.particularsDaily.length > 0) {
        if (particularsDaily == null) {particularsDaily = s.particularsDaily;}
        else {particularsDaily = [...particularsDaily, ...s.particularsDaily];}
      }
      if (s.particularsMacros != null && typeof s.particularsMacros === 'object' && !Array.isArray(s.particularsMacros)) {
        Object.assign(particularsMacros, s.particularsMacros);
      }
      if (s.thumbnail != null) {merged.thumbnail = s.thumbnail;}
      if (s.attachments != null) {merged.attachments = s.attachments;}
      if (s.coupon != null) {merged.coupon = s.coupon;}
      if (s.used_free_consultations != null) {merged.used_free_consultations = s.used_free_consultations;}
      if (s.used_free_head_consultations != null) {merged.used_free_head_consultations = s.used_free_head_consultations;}
      if (s.image != null) {merged.image = s.image;}
      if (s.subscription_detail != null) {merged.subscription_detail = s.subscription_detail;}
      if (s.title != null) {merged.title = s.title;}
      if (s.name != null) {merged.name = s.name;}
      if (s.description != null) {merged.description = s.description;}
      if (s.category != null) {merged.category = s.category;}
    }

    if (particularsDaily != null) {merged.particularsDaily = particularsDaily;}
    if (Object.keys(particularsMacros).length > 0) {merged.particularsMacros = particularsMacros;}

    if (merged.detail == null) {merged.detail = {};}
    result.push(merged);
  }

  return result.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

/** Resolve plan image from merged raw row (3 API sources); handles attachments HTML and CMS field variants. */
function resolveMergedSubscriptionImage(raw: MergedRawSubscription): string | undefined {
  const detail = raw.detail ?? {};
  const picked =
    pickCmsPageImageUri(raw as unknown as Record<string, unknown>) ??
    pickCmsPageImageUri(detail as Record<string, unknown>) ??
    (raw.subscription_detail != null && typeof raw.subscription_detail === 'object'
      ? pickCmsPageImageUri(raw.subscription_detail as Record<string, unknown>)
      : null);
  if (picked) {
    return picked;
  }
  const candidates = [
    raw.image,
    raw.thumbnail,
    raw.attachments,
    detail.image,
    (detail as { thumbnail?: string }).thumbnail,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      const resolved = resolveImageUri(candidate);
      if (resolved) {
        return resolved;
      }
    }
  }
  return undefined;
}

/** Map merged raw item to SubscriptionData. Keeps normalized day_off, selection_limit, parseApiDate. */
function mapMergedRawSubscriptionToSubscriptionData(
  raw: MergedRawSubscription,
  customer_id: number,
  pause_effective_date?: string | null,
): SubscriptionData {
  const start_date = parseApiDate(raw.start_date) || (raw.start_date ?? '');
  const end_date = parseApiDate(raw.end_date) || (raw.end_date ?? '');
  const detail = raw.detail ?? {};
  const dayOff = detail.day_off;
  const selection_limit = buildSelectionLimitRecord(detail.selection_limit);
  const merged_image = resolveMergedSubscriptionImage(raw);

  return {
    id: raw.id,
    customer_id: raw.customer_id ?? customer_id,
    start_date,
    end_date,
    status: raw.status ?? 'unknown',
    pause_effective_date: pause_effective_date ?? undefined,
    freeze_dates: asYmdDateList(raw.freeze_dates),
    /** Canonical pause list = API `paused_dates` (particulars no longer drive UI). */
    pause_dates: asYmdDateList(raw.paused_dates),
    paused_dates: asYmdDateList(raw.paused_dates),
    skipped_dates: asYmdDateList(raw.skipped_dates),
    double_box_dates: asYmdDateList(raw.double_box_dates),
    used_freeze_count: raw.used_freeze_count,
    cost: raw.cost ?? (raw as { price?: number }).price,
    title: detail.title ?? raw.title ?? raw.name,
    description: detail.description ?? raw.description,
    category: raw.category,
    detail: {
      day: detail.day != null ? Number(detail.day) : undefined,
      days: detail.days != null ? Number(detail.days) : undefined,
      /** Empty when API omits day_off — normalizeDayOff([]) falls back to org week_day_off (not Sunday=0). */
      day_off: dayOff != null ? dayOff : [],
      total_meals: detail.total_meals ?? 0,
      total_snacks: detail.total_snacks ?? 0,
      minimum_calories: detail.minimum_calories ?? 0,
      maximum_calories: detail.maximum_calories ?? 9999999,
      maximum_carbs: detail.maximum_carbs ?? 0,
      default_carbs: detail.default_carbs ?? 50,
      is_variable_carbs: detail.is_variable_carbs ?? false,
      force_fixed_menu: detail.force_fixed_menu ?? false,
      allowed_freeze_days: detail.allowed_freeze_days,
      selection_limit,
      cost: detail.cost,
      title: detail.title,
      arabic_title: detail.arabic_title ?? detail.a_title,
      a_title: detail.a_title ?? detail.arabic_title,
      description: detail.description,
      arabic_description: detail.arabic_description,
      maximum_protein: detail.maximum_protein ?? 0,
      image: merged_image ?? detail.image ?? (detail as { thumbnail?: string }).thumbnail,
      thumbnail: (detail as { thumbnail?: string }).thumbnail ?? raw.thumbnail,
      day_left: detail.day_left != null ? Number(detail.day_left) : undefined,
      is_upgradable: Boolean(detail.is_upgradable),
      is_subscription_upgraded: Boolean(detail.is_subscription_upgraded),
      upgradable_food_types: Array.isArray(detail.upgradable_food_types)
        ? detail.upgradable_food_types
        : undefined,
      upgradable_meal_types: Array.isArray(detail.upgradable_meal_types)
        ? detail.upgradable_meal_types
        : undefined,
    },
    particularsMacros:
      raw.particularsMacros != null && typeof raw.particularsMacros === 'object'
        ? (raw.particularsMacros as Record<string, unknown>)
        : undefined,
    particularsDaily: Array.isArray(raw.particularsDaily) ? raw.particularsDaily : undefined,
    subscription_detail: raw.subscription_detail,
    attachments: raw.attachments,
    slug: raw.slug,
    identifier: raw.identifier,
    image: merged_image,
    thumbnail: raw.thumbnail ?? merged_image,
  };
}

/** Filter to active (status === 'activated') only. */
function filterMergedSubscriptionsToActiveOnly(merged: MergedRawSubscription[]): MergedRawSubscription[] {
  return merged.filter(m => m.status === 'activated');
}

function resolveProbeSubscriptionFilter(merged: MergedRawSubscription[]): {
  mode: string;
  eligible: MergedRawSubscription[];
  pause_effective_by_id: Map<number, string | null>;
} {
  try {
    const { getSubscriptionMenuProbeModeSync } =
      require('../../../exDev/flags/subscriptionMenuProbe') as typeof import('../../../exDev/flags/subscriptionMenuProbe');
    const { filterMergedSubscriptionsForProbe } =
      require('../../../exDev/probes/subscriptionMenuProbe/logic') as typeof import('../../../exDev/probes/subscriptionMenuProbe/logic');
    const mode = getSubscriptionMenuProbeModeSync();
    if (mode === 'disabled') {
      return {
        mode,
        eligible: filterMergedSubscriptionsToActiveOnly(merged),
        pause_effective_by_id: new Map(),
      };
    }
    const result = filterMergedSubscriptionsForProbe(merged, mode);
    return { mode, eligible: result.eligible, pause_effective_by_id: result.pause_effective_by_id };
  } catch {
    return {
      mode: 'disabled',
      eligible: filterMergedSubscriptionsToActiveOnly(merged),
      pause_effective_by_id: new Map(),
    };
  }
}

function isSubscriptionMenuEligibleOnDateForProbe(sub: SubscriptionData, date_str: string, probe_mode: string): boolean {
  if (probe_mode !== 'effective_date') {
    return true;
  }
  try {
    const { isActivatedSubscriptionStatus, isPausedLikeSubscriptionStatus, isTerminalSubscriptionStatus } =
      require('../../../exDev/probes/subscriptionMenuProbe/logic') as typeof import('../../../exDev/probes/subscriptionMenuProbe/logic');
    if (isTerminalSubscriptionStatus(sub.status)) {
      return false;
    }
    if (isActivatedSubscriptionStatus(sub.status)) {
      return true;
    }
    if (isPausedLikeSubscriptionStatus(sub.status)) {
      const effective = sub.pause_effective_date;
      if (!effective) {
        return false;
      }
      const day = moment(date_str, 'YYYY-MM-DD', true);
      const effective_day = moment(effective, 'YYYY-MM-DD', true);
      return day.isValid() && effective_day.isValid() && day.isBefore(effective_day, 'day');
    }
    return false;
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Date/calendar calculations (unchanged)
// ---------------------------------------------------------------------------

function calculateAvailableDates(
  subscriptions: SubscriptionData[],
  config: DateServiceConfig,
  probe_mode = 'disabled',
): string[] {
  const today = moment().startOf('day');
  const allValidDates: string[] = [];

  for (const subscription of subscriptions) {
    const { start_date, end_date, freeze_dates, detail } = subscription;
    const freezeSet = new Set(freeze_dates ?? []);
    const pauseSet = new Set(
      (Array.isArray(subscription.paused_dates)
        ? subscription.paused_dates
        : (subscription.pause_dates ?? [])
      ).map(d => String(d).trim()).filter(Boolean),
    );
    const skipSet = new Set((subscription.skipped_dates ?? []).map(d => String(d).trim()).filter(Boolean));
    // skip > pause for available-date math
    for (const d of skipSet) {
      pauseSet.delete(d);
    }
    const double_box_anchors = collectDoubleBoxAnchorDates([subscription]);
    const double_consumed = new Set(
      getDeliveryDatesConsumedByDoubleBox(
        [],
        date => double_box_anchors.includes(date),
        double_box_anchors,
      ),
    );
    const subscriptionDayOff = normalizeDayOff(detail?.day_off);
    const hasSubscriptionDayOff = subscriptionDayOff.length > 0;
    const hasGlobalDayOff = config.week_day_off !== 0;
    const dateRange = getDateRange(start_date, end_date);
    const subscriptionStartMoment = moment(start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);

    const filteredDates = dateRange.filter(dateStr => {
      const mDate = moment(dateStr, 'YYYY-MM-DD', true);
      if (!mDate.isValid()) {return false;}
      const momentDayOfWeek = mDate.day();
      const customDayOfWeek = momentDayOfWeek === 0 ? 7 : momentDayOfWeek;

      let isDayOff = false;
      if (hasSubscriptionDayOff) {
        isDayOff = subscriptionDayOff.includes(momentDayOfWeek);
      } else if (hasGlobalDayOff) {
        isDayOff = customDayOfWeek === config.week_day_off;
      }

      if (freezeSet.has(dateStr)) {return false;}
      if (skipSet.has(dateStr)) {return false;}
      if (pauseSet.has(dateStr)) {return false;}
      if (double_consumed.has(dateStr)) {return false;}
      if (isDayOff) {return false;}
      if (!isSubscriptionMenuEligibleOnDateForProbe(subscription, dateStr, probe_mode)) {return false;}
      if (mDate.isBefore(today)) {return false;}

      const minSelectableDate = today.clone().add(config.menu_selection_before, 'days');
      if (subscriptionStartMoment.isAfter(today)) {
        const effectiveMinDate = moment.max(minSelectableDate, subscriptionStartMoment);
        return mDate.isSameOrAfter(effectiveMinDate);
      }
      return mDate.isSameOrAfter(minSelectableDate);
    });

    allValidDates.push(...filteredDates);
  }

  return [...new Set(allValidDates)].sort((a, b) => moment(a).diff(moment(b)));
}

function generateDayConfigs(subscriptions: SubscriptionData[], dates: string[]): Record<string, DayConfig> {
  const dayConfigs: Record<string, DayConfig> = {};
  const weekDayArray = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const double_box_set = new Set(
    subscriptions.flatMap(s =>
      (s.double_box_dates ?? []).map(d => {
        const raw = String(d).trim();
        return ensureAsciiDateString(parseApiDate(raw) || raw) || parseApiDate(raw) || raw;
      }).filter(Boolean),
    ),
  );

  for (const date of dates) {
    const currentDate = moment(date, 'YYYY-MM-DD', true);
    if (!currentDate.isValid()) {continue;}

    for (const subscription of subscriptions) {
      const startDate = moment(subscription.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      const endDate = moment(subscription.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      if (!startDate.isValid() || !endDate.isValid()) {continue;}
      if (!currentDate.isBetween(startDate, endDate, 'day', '[]')) {continue;}

      const detail = subscription.detail ?? ({} as SubscriptionData['detail']);
      const lastDate = subscription.end_date
        ? moment(subscription.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true).format('YYYY-MM-DD')
        : '';
      const firstDayOff = (() => {
        const no = normalizeDayOff(detail.day_off);
        return no.length > 0 ? no[0]! : 0;
      })();

      const selectionLimit = (detail.selection_limit as Record<number, number>) ?? {};
      const default_carbs_n = Number(detail.default_carbs);
      const maximum_carbs_n = Number(detail.maximum_carbs);
      // `Number(x) ?? 50` is wrong: NaN is not nullish. Prefer positive default_carbs.
      const default_scaled_carbs =
        Number.isFinite(default_carbs_n) && default_carbs_n > 0 ? Math.round(default_carbs_n) : 50;
      const max_scaled_carbs =
        Number.isFinite(maximum_carbs_n) && maximum_carbs_n > 0 ? Math.round(maximum_carbs_n) : 0;
      dayConfigs[date] = {
        date,
        isDoubleBox: double_box_set.has(date),
        is_variable_carbs: detail.is_variable_carbs ?? false,
        isForceFixedMenu: detail.force_fixed_menu ?? false,
        selectionLimit,
        selectedSubscriptionID: subscription.id,
        mealsCount: Number(detail.total_meals) ?? 0,
        snacksCount: Number(detail.total_snacks) ?? 0,
        min_calories: Number(detail.minimum_calories) ?? 0,
        max_calories: Number(detail.maximum_calories) ?? 9999999,
        max_scaled_carbs,
        default_scaled_carbs,
        weekDayOff: firstDayOff,
        weekDay: weekDayArray[firstDayOff] ?? '',
        subscriptLastDate: lastDate,
      };
      break;
    }
  }

  return dayConfigs;
}

function extractAllFreezeDates(subscriptions: SubscriptionData[]): string[] {
  const all: string[] = [];
  for (const sub of subscriptions) {
    if (Array.isArray(sub.freeze_dates)) {all.push(...sub.freeze_dates);}
  }
  return [...new Set(all)].sort((a, b) => moment(a).diff(moment(b)));
}

function extractAllPauseDates(subscriptions: SubscriptionData[]): string[] {
  const all: string[] = [];
  for (const sub of subscriptions) {
    const skipped = new Set((sub.skipped_dates ?? []).map(d => String(d).trim()).filter(Boolean));
    const paused = Array.isArray(sub.paused_dates) ? sub.paused_dates : (sub.pause_dates ?? []);
    for (const d of paused) {
      const s = String(d).trim();
      if (s && !skipped.has(s)) {
        all.push(s);
      }
    }
  }
  return [...new Set(all)].sort((a, b) => moment(a).diff(moment(b)));
}

function calculateMinMaxDates(
  subscriptions: SubscriptionData[],
  config: DateServiceConfig,
): { minDate: string; maxDate: string } {
  const today = moment().startOf('day');
  let minDate = today.clone().add(config.menu_selection_before, 'days').format('YYYY-MM-DD');
  let maxDate = moment().add(1, 'years').format('YYYY-MM-DD');

  if (subscriptions.length > 0) {
    let earliestStart: moment.Moment | null = null;
    let latestEnd: moment.Moment | null = null;
    for (const sub of subscriptions) {
      const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      if (start.isValid() && (!earliestStart || start.isBefore(earliestStart))) {earliestStart = start.clone();}
      if (end.isValid() && (!latestEnd || end.isAfter(latestEnd))) {latestEnd = end.clone();}
    }
    if (earliestStart && earliestStart.isAfter(today)) {minDate = earliestStart.format('YYYY-MM-DD');}
    if (latestEnd) {maxDate = latestEnd.format('YYYY-MM-DD');}
  }

  return { minDate, maxDate };
}

function calculateCheckoutMinMaxDates(config: DateServiceConfig): {
  minDateForCheckout: string;
  maxDateForCheckout: string;
} {
  const today = moment().startOf('day');
  return {
    minDateForCheckout: today.clone().add(config.package_start_after, 'days').format('YYYY-MM-DD'),
    maxDateForCheckout: moment().add(1, 'years').format('YYYY-MM-DD'),
  };
}

function extractActiveDateRanges(subscriptions: SubscriptionData[]): Array<{ start: string; end: string }> {
  const ranges: Array<{ start: string; end: string }> = [];
  for (const sub of subscriptions) {
    const start_m = parseSubscriptionApiDate(sub.start_date);
    const end_m = parseSubscriptionApiDate(sub.end_date);
    if (!start_m || !end_m) {
      continue;
    }
    ranges.push({
      start: start_m.format('YYYY-MM-DD'),
      end: end_m.format('YYYY-MM-DD'),
    });
  }
  return ranges;
}
export type SubscriptionMergeInputs = {
  sub_data: CustomerSubscriptionsFlatApiResponse | undefined;
  sub_new: CustomerSubscriptionsNewApiResponse | undefined;
  sub_list: CustomerSubscriptionsListApiResponse | undefined;
  customer_id: number;
};

/** Build merged SubscriptionDataResponse from three normalized endpoint payloads. */
export function buildSubscriptionDataResponse(input: SubscriptionMergeInputs): SubscriptionDataResponse {
  const config = getDateServiceConfig();
  const { sub_data, sub_new, sub_list, customer_id } = input;

  const prev_locale = moment.locale();
  try {
    moment.locale('en');

    const mapByIdFromNew = flattenSubscriptionsNewResponseToMapById(sub_new);
    const mapByIdFromFlat = flattenSubscriptionsFlatResponseToMapById(sub_data);
    const mapByIdFromList = flattenSubscriptionsListResponseToMapById(sub_list);

    const mergedSubscriptions = mergeSubscriptionMapsById(mapByIdFromNew, mapByIdFromFlat, mapByIdFromList);
    const probe_filter = resolveProbeSubscriptionFilter(mergedSubscriptions);
    const subscriptions = probe_filter.eligible.map(raw =>
      mapMergedRawSubscriptionToSubscriptionData(
        raw,
        customer_id,
        probe_filter.pause_effective_by_id.get(raw.id) ?? null,
      ),
    );

    const availableDates = calculateAvailableDates(subscriptions, config, probe_filter.mode);
    const dayConfigMap = generateDayConfigs(subscriptions, availableDates);
    const freezeDates = extractAllFreezeDates(subscriptions);
    const pauseDates = extractAllPauseDates(subscriptions);
    const { minDate, maxDate } = calculateMinMaxDates(subscriptions, config);
    const { minDateForCheckout, maxDateForCheckout } = calculateCheckoutMinMaxDates(config);
    const activeDateRanges = extractActiveDateRanges(subscriptions);

    return {
      subscriptions,
      availableDates,
      dayConfigMap,
      freezeDates,
      pauseDates,
      minDate,
      maxDate,
      minDateForCheckout,
      maxDateForCheckout,
      activeDateRanges,
      config,
    };
  } finally {
    moment.locale(prev_locale);
  }
}

export { normalizeDayOff, buildSelectionLimitRecord };
